import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, Table, Button, Badge, Spinner, Modal, Alert, Row, Col, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { notifyTradeExecution } from '../../../shared/utils/tradeNotifications';
import { formatToLocalTime } from '../../../shared/utils/dateUtils';

const HoldingsManager = ({ profileId, token, isActive, onAlert, onRefresh }) => {
    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    const navigate = useNavigate();

    const dateTimeFormatOptions = {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };

    const [holdings, setHoldings] = useState([]);
    const [portfolio, setPortfolio] = useState(null);
    const [portfolioSummary, setPortfolioSummary] = useState(null);
    const [trades, setTrades] = useState([]);
    const [holdingOrderBreakdown, setHoldingOrderBreakdown] = useState({});
    const [tradeHistoryTrades, setTradeHistoryTrades] = useState([]);
    const [openLimitOrders, setOpenLimitOrders] = useState([]);
    const [openRiskSettings, setOpenRiskSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTradeHistory, setShowTradeHistory] = useState(false);
    const [tradeHistoryLoading, setTradeHistoryLoading] = useState(false);
    const [tradeHistoryError, setTradeHistoryError] = useState('');
    const [tradeHistoryDate, setTradeHistoryDate] = useState('');
    const [tradeHistoryFromDate, setTradeHistoryFromDate] = useState('');
    const [tradeHistoryToDate, setTradeHistoryToDate] = useState('');
    const [tradeHistorySymbol, setTradeHistorySymbol] = useState('');
    const [tradeHistoryType, setTradeHistoryType] = useState('');
    const [tradeHistoryOrder, setTradeHistoryOrder] = useState('');
    const [tradeHistoryStatus, setTradeHistoryStatus] = useState('');
    const [sellQuantity, setSellQuantity] = useState({});
    const [showSellModal, setShowSellModal] = useState(false);
    const [selectedHolding, setSelectedHolding] = useState(null);
    const [modalQuantity, setModalQuantity] = useState(0);
    const [submittingSell, setSubmittingSell] = useState(false);
    const [lastFetchedAt, setLastFetchedAt] = useState(0);
    const [cancelingOrderId, setCancelingOrderId] = useState(null);
    const [showEditOrderModal, setShowEditOrderModal] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [editQuantity, setEditQuantity] = useState('');
    const [editLimitPrice, setEditLimitPrice] = useState('');
    const [editExpiry, setEditExpiry] = useState('');
    const [editExpiryPreset, setEditExpiryPreset] = useState('custom');
    const [savingEdit, setSavingEdit] = useState(false);
    const [showEditRiskModal, setShowEditRiskModal] = useState(false);
    const [editingRiskOrder, setEditingRiskOrder] = useState(null);
    const [editStopLoss, setEditStopLoss] = useState('');
    const [editTakeProfit, setEditTakeProfit] = useState('');
    const [savingRisk, setSavingRisk] = useState(false);
    const [showCreateRiskModal, setShowCreateRiskModal] = useState(false);
    const [createRiskHolding, setCreateRiskHolding] = useState(null);
    const [createRiskQuantity, setCreateRiskQuantity] = useState('');
    const [createRiskStopLoss, setCreateRiskStopLoss] = useState('');
    const [createRiskTakeProfit, setCreateRiskTakeProfit] = useState('');
    const [savingCreateRisk, setSavingCreateRisk] = useState(false);
    const notifiedCanceledIdsRef = useRef(new Set());
    const tradeStatusRef = useRef(new Map());

    const authHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        if (profileId && token) {
            fetchData();
        }
    }, [profileId, token]);

    useEffect(() => {
        if (!profileId || !token) return;
        const handlePaperTradingUpdate = () => {
            fetchData();
        };
        window.addEventListener('paperTradingUpdated', handlePaperTradingUpdate);
        return () => window.removeEventListener('paperTradingUpdated', handlePaperTradingUpdate);
    }, [profileId, token]);

    useEffect(() => {
        if (!isActive || !profileId || !token) return;
        const updatedAt = parseInt(localStorage.getItem('paperTradingUpdatedAt') || '0', 10);
        if (updatedAt > lastFetchedAt) {
            fetchData();
        }
    }, [isActive, profileId, token, lastFetchedAt]);

    useEffect(() => {
        if (!isActive || !profileId || !token) return;
        const intervalId = setInterval(() => {
            fetchData();
        }, 60 * 1000);
        return () => clearInterval(intervalId);
    }, [isActive, profileId, token]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [portfolioRes, holdingsRes, summaryRes, openOrdersRes, canceledOrdersRes, tradesRes] = await Promise.all([
                fetch(`${API_BASE}/api/paper-trading/profiles/${profileId}/portfolio`, { headers: authHeaders }),
                fetch(`${API_BASE}/api/paper-trading/profiles/${profileId}/holdings`, { headers: authHeaders }),
                fetch(`${API_BASE}/api/paper-trading/profiles/${profileId}/portfolio/summary`, { headers: authHeaders }),
                fetch(`${API_BASE}/api/paper-trading/profiles/${profileId}/trades?status=OPEN&limit=200`, { headers: authHeaders }),
                fetch(`${API_BASE}/api/paper-trading/profiles/${profileId}/trades?status=CANCELED&limit=50`, { headers: authHeaders }),
                fetch(`${API_BASE}/api/paper-trading/profiles/${profileId}/trades?limit=200`, { headers: authHeaders })
            ]);

            if (portfolioRes.ok) {
                const portfolioData = await portfolioRes.json();
                setPortfolio(portfolioData);
            }

            if (holdingsRes.ok) {
                const holdingsData = await holdingsRes.json();
                setHoldings(holdingsData);
                fetchHoldingOrderBreakdown(holdingsData);
            }

            if (summaryRes.ok) {
                const summaryData = await summaryRes.json();
                setPortfolioSummary(summaryData);
            }

            if (openOrdersRes.ok) {
                const openOrders = await openOrdersRes.json();
                const limitOrders = Array.isArray(openOrders)
                    ? openOrders.filter(order => order.order_type === 'LIMIT')
                    : [];
                setOpenLimitOrders(limitOrders);
                const riskSettings = Array.isArray(openOrders)
                    ? openOrders.filter(order =>
                        order.order_type !== 'LIMIT' &&
                        order.trade_type === 'SELL' &&
                        (order.stop_loss !== null && order.stop_loss !== undefined ||
                         order.take_profit !== null && order.take_profit !== undefined)
                      )
                    : [];
                setOpenRiskSettings(riskSettings);
            } else {
                setOpenLimitOrders([]);
                setOpenRiskSettings([]);
            }

            if (canceledOrdersRes.ok) {
                const canceledOrders = await canceledOrdersRes.json();
                const expiredLimitOrders = Array.isArray(canceledOrders)
                    ? canceledOrders.filter(order => order.order_type === 'LIMIT' && order.close_reason === 'EXPIRED')
                    : [];
                expiredLimitOrders.forEach((order) => {
                    if (!notifiedCanceledIdsRef.current.has(order.id)) {
                        notifiedCanceledIdsRef.current.add(order.id);
                        const expiresAt = order.expires_at ? new Date(order.expires_at) : null;
                        const message = `${order.trade_type} ${order.quantity} ${order.ticker_symbol} expired${order.limit_price ? ` at ${formatCurrency(order.limit_price)}` : ''}`;
                        const details = [
                            order.limit_price ? `Limit: ${formatCurrency(order.limit_price)}` : null,
                            expiresAt ? `Valid until: ${formatToLocalTime(expiresAt.toISOString())}` : null
                        ].filter(Boolean).join(' • ');
                        notifyTradeExecution(
                            'Limit Order Expired',
                            details ? `${message} (${details})` : message,
                            'HIGH'
                        );
                        if (onAlert) {
                            onAlert({
                                id: `expired-${order.id}-${order.closed_at || order.expires_at || order.created_at || Date.now()}`,
                                title: 'Limit Order Expired',
                                message,
                                details,
                                sourceType: 'trade',
                                sourceId: `${order.id}-expired`,
                                type: 'expired',
                                severity: 'HIGH',
                                timestamp: Date.now()
                            });
                        }
                    }
                });
            }



            if (tradesRes.ok) {
                const tradesData = await tradesRes.json();
                if (Array.isArray(tradesData)) {
                    setTrades(tradesData);
                    tradesData.forEach((trade) => {
                        const prevStatus = tradeStatusRef.current.get(trade.id);
                        if (prevStatus && prevStatus !== trade.status) {
                            const message = `${trade.trade_type} ${trade.quantity} ${trade.ticker_symbol}: ${prevStatus} → ${trade.status}${trade.exit_price ? ` at ${formatCurrency(trade.exit_price)}` : trade.entry_price ? ` at ${formatCurrency(trade.entry_price)}` : ''}`;
                            const details = [
                                trade.order_type ? `Order: ${trade.order_type}` : null,
                                trade.limit_price ? `Limit: ${formatCurrency(trade.limit_price)}` : null,
                                trade.entry_price ? `Entry: ${formatCurrency(trade.entry_price)}` : null,
                                trade.exit_price ? `Exit: ${formatCurrency(trade.exit_price)}` : null,
                                trade.close_reason ? `Reason: ${trade.close_reason}` : null
                            ].filter(Boolean).join(' • ');
                            notifyTradeExecution(
                                'Trade Update',
                                details ? `${message} (${details})` : message,
                                'LOW'
                            );
                            if (onAlert) {
                                onAlert({
                                    id: `status-${trade.id}-${trade.status}-${trade.closed_at || trade.created_at || Date.now()}`,
                                    title: 'Trade Update',
                                    message,
                                    details,
                                    sourceType: 'trade',
                                    sourceId: `${trade.id}-status-${trade.status}`,
                                    type: 'status_change',
                                    severity: 'LOW',
                                    timestamp: Date.now()
                                });
                            }
                        }
                        tradeStatusRef.current.set(trade.id, trade.status);
                    });
                }
            }
            setLastFetchedAt(Date.now());
        } catch (error) {
            console.error('Error fetching paper trading data:', error);
            toast.error('Failed to load portfolio data');
        } finally {
            setLoading(false);
        }
    };

    const fetchHoldingOrderBreakdown = async (holdingsData) => {
        const normalizedHoldings = Array.isArray(holdingsData) ? holdingsData : [];
        const symbolToHolding = {};
        normalizedHoldings.forEach((holding) => {
            const symbol = (holding?.ticker_symbol || '').toUpperCase();
            if (symbol) {
                symbolToHolding[symbol] = holding;
            }
        });
        const symbols = Object.keys(symbolToHolding);
        if (symbols.length === 0) {
            setHoldingOrderBreakdown({});
            return;
        }

        const pageSize = 200;
        let offset = 0;
        const targetSymbols = new Set(symbols);
        const trades = [];

        try {
            while (true) {
                const response = await fetch(
                    `${API_BASE}/api/paper-trading/profiles/${profileId}/trades?limit=${pageSize}&offset=${offset}`,
                    { headers: authHeaders }
                );
                if (!response.ok) {
                    break;
                }
                const data = await response.json();
                const batch = Array.isArray(data) ? data : [];
                if (batch.length === 0) {
                    break;
                }

                batch.forEach((trade) => {
                    const symbol = (trade?.ticker_symbol || '').toUpperCase();
                    if (!symbol || !targetSymbols.has(symbol)) return;
                    trades.push(trade);
                });

                if (batch.length < pageSize) {
                    break;
                }
                offset += pageSize;
            }

            const sortKeyForTrade = (trade) => {
                const isSell = String(trade?.trade_type || '').toUpperCase() === 'SELL';
                const ts = isSell
                    ? (trade?.closed_at || trade?.created_at)
                    : trade?.created_at;
                const ms = ts ? new Date(ts).getTime() : NaN;
                return Number.isFinite(ms) ? ms : 0;
            };

            const sortedTrades = [...trades].sort((a, b) => {
                const ta = sortKeyForTrade(a);
                const tb = sortKeyForTrade(b);
                if (ta !== tb) return ta - tb;
                return Number(a?.id || 0) - Number(b?.id || 0);
            });

            const openLotsBySymbol = {};
            sortedTrades.forEach((trade) => {
                const symbol = (trade?.ticker_symbol || '').toUpperCase();
                if (!symbol || !targetSymbols.has(symbol)) return;
                const tradeType = String(trade?.trade_type || '').toUpperCase();
                const status = String(trade?.status || '').toUpperCase();
                const qty = Number(trade?.quantity || 0);
                if (qty <= 0 || status !== 'CLOSED') return;

                if (tradeType === 'BUY') {
                    const orderType = (trade.order_type || 'MARKET').toUpperCase();
                    const entryPrice = Number(trade.entry_price || 0);
                    if (entryPrice <= 0) return;
                    if (!openLotsBySymbol[symbol]) openLotsBySymbol[symbol] = [];
                    openLotsBySymbol[symbol].push({
                        order_type: orderType,
                        remaining: qty,
                        entry_price: entryPrice,
                    });
                    return;
                }

                if (tradeType === 'SELL') {
                    let remainingToConsume = qty;
                    const lots = openLotsBySymbol[symbol] || [];
                    for (let i = 0; i < lots.length && remainingToConsume > 0; i += 1) {
                        const lot = lots[i];
                        if (!lot || lot.remaining <= 0) continue;
                        const consumed = Math.min(lot.remaining, remainingToConsume);
                        lot.remaining -= consumed;
                        remainingToConsume -= consumed;
                    }
                }
            });

            const breakdown = {};
            symbols.forEach((symbol) => {
                const holding = symbolToHolding[symbol];
                const currentQty = Number(holding?.quantity || 0);
                const lots = (openLotsBySymbol[symbol] || []).filter((lot) => Number(lot.remaining || 0) > 0);
                if (currentQty <= 0 || lots.length === 0) {
                    return;
                }

                const orderStats = {};
                lots.forEach((lot) => {
                    const orderType = (lot.order_type || 'MARKET').toUpperCase();
                    if (!orderStats[orderType]) {
                        orderStats[orderType] = { quantity: 0, total_invested: 0 };
                    }
                    orderStats[orderType].quantity += Number(lot.remaining || 0);
                    orderStats[orderType].total_invested += Number(lot.remaining || 0) * Number(lot.entry_price || 0);
                });

                let finalGroups = Object.entries(orderStats)
                    .map(([order_type, stat]) => ({
                        order_type,
                        quantity: Number(stat.quantity || 0),
                        total_invested: Number(stat.total_invested || 0),
                    }))
                    .filter((item) => item.quantity > 0 && item.total_invested > 0);

                if (finalGroups.length > 0) {
                    breakdown[symbol] = finalGroups
                        .sort((a, b) => b.quantity - a.quantity)
                        .map((item) => ({
                            ...item,
                            quantity: Number(item.quantity || 0),
                            total_invested: Number(item.total_invested || 0),
                        }));
                }
            });

            setHoldingOrderBreakdown(breakdown);
        } catch (error) {
            console.error('Error fetching holding order breakdown:', error);
        }
    };

    const fetchTradeHistory = async () => {
        const pageSize = 200;
        let offset = 0;
        let allTrades = [];
        setTradeHistoryLoading(true);
        setTradeHistoryError('');
        try {
            while (true) {
                const response = await fetch(
                    `${API_BASE}/api/paper-trading/profiles/${profileId}/trades?limit=${pageSize}&offset=${offset}`,
                    { headers: authHeaders }
                );
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || 'Failed to fetch trade history');
                }
                const data = await response.json();
                const batch = Array.isArray(data) ? data : [];
                allTrades = allTrades.concat(batch);
                if (batch.length < pageSize) break;
                offset += pageSize;
            }
            setTradeHistoryTrades(allTrades);
        } catch (error) {
            console.error('Error fetching trade history:', error);
            setTradeHistoryError(error.message || 'Failed to load trade history');
        } finally {
            setTradeHistoryLoading(false);
        }
    };

    const filteredTradeHistory = useMemo(() => {
        if (!Array.isArray(tradeHistoryTrades) || tradeHistoryTrades.length === 0) {
            return [];
        }

        let startDate = null;
        let endDate = null;

        if (tradeHistoryDate) {
            startDate = new Date(`${tradeHistoryDate}T00:00:00`);
            endDate = new Date(`${tradeHistoryDate}T23:59:59.999`);
        } else {
            if (tradeHistoryFromDate) {
                startDate = new Date(`${tradeHistoryFromDate}T00:00:00`);
            }
            if (tradeHistoryToDate) {
                endDate = new Date(`${tradeHistoryToDate}T23:59:59.999`);
            }
        }

        return tradeHistoryTrades.filter((trade) => {
            const tradeDate = new Date(trade.created_at);
            if (Number.isNaN(tradeDate.getTime())) return false;
            if (startDate && tradeDate < startDate) return false;
            if (endDate && tradeDate > endDate) return false;
            if (tradeHistorySymbol && (trade?.ticker_symbol || '').toUpperCase() !== tradeHistorySymbol) return false;
            if (tradeHistoryType && (trade?.trade_type || '').toUpperCase() !== tradeHistoryType) return false;
            if (tradeHistoryOrder && (trade?.order_type || 'MARKET').toUpperCase() !== tradeHistoryOrder) return false;
            if (tradeHistoryStatus && (trade?.status || '').toUpperCase() !== tradeHistoryStatus) return false;
            return true;
        });
    }, [tradeHistoryTrades, tradeHistoryDate, tradeHistoryFromDate, tradeHistoryToDate, tradeHistorySymbol, tradeHistoryType, tradeHistoryOrder, tradeHistoryStatus]);

    const tradeHistorySymbols = useMemo(() => {
        const symbols = new Set();
        tradeHistoryTrades.forEach((trade) => {
            const symbol = (trade?.ticker_symbol || '').toUpperCase();
            if (symbol) symbols.add(symbol);
        });
        return Array.from(symbols).sort();
    }, [tradeHistoryTrades]);

    const tradeHistoryOrders = useMemo(() => {
        const orders = new Set();
        tradeHistoryTrades.forEach((trade) => {
            const orderType = (trade?.order_type || 'MARKET').toUpperCase();
            if (orderType) orders.add(orderType);
        });
        return Array.from(orders).sort();
    }, [tradeHistoryTrades]);

    const tradeHistoryStatuses = useMemo(() => {
        const statuses = new Set();
        tradeHistoryTrades.forEach((trade) => {
            const status = (trade?.status || '').toUpperCase();
            if (status) statuses.add(status);
        });
        return Array.from(statuses).sort();
    }, [tradeHistoryTrades]);

    const holdingDisplayRows = useMemo(() => {
        const rows = [];
        holdings.forEach((holding) => {
            const symbol = (holding?.ticker_symbol || '').toUpperCase();
            const groups = holdingOrderBreakdown[symbol] || [];
            const currentPrice = Number(holding?.current_price || 0);
            const totalQty = Number(holding?.quantity || 0);
            const fallbackTotalInvested = Number(holding?.total_invested || 0);

            if (!groups.length) {
                rows.push({
                    ...holding,
                    display_key: `${holding.id}-default`,
                    display_order_type: '-',
                    is_grouped: false,
                });
                return;
            }

            groups.forEach((group, index) => {
                const groupQty = Number(group.quantity || 0);
                const groupInvested = Number(group.total_invested || 0);
                const currentValue = currentPrice * groupQty;
                const profitLoss = currentValue - groupInvested;
                const profitLossPercent = groupInvested > 0 ? (profitLoss / groupInvested) * 100 : 0;

                rows.push({
                    ...holding,
                    quantity: groupQty,
                    total_invested: groupInvested || (totalQty > 0 ? (fallbackTotalInvested * groupQty) / totalQty : 0),
                    average_cost: groupQty > 0 ? groupInvested / groupQty : holding.average_cost,
                    current_value: currentValue,
                    profit_loss: profitLoss,
                    profit_loss_percent: profitLossPercent,
                    display_key: `${holding.id}-${group.order_type}-${index}`,
                    display_order_type: group.order_type,
                    is_grouped: true,
                });
            });
        });
        return rows;
    }, [holdings, holdingOrderBreakdown]);

    const handleSell = async (holding, quantity) => {
        const qty = quantity;

        if (qty <= 0 || qty > holding.quantity) {
            toast.error('Invalid quantity');
            return;
        }

        try {
            setSubmittingSell(true);
            const response = await fetch(
                `${API_BASE}/api/paper-trading/profiles/${profileId}/trades/sell`,
                {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify({
                        ticker_symbol: holding.ticker_symbol,
                        quantity: parseInt(qty)
                    })
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Sell order failed');
            }

            toast.success(`Sold ${qty} shares of ${holding.ticker_symbol}`);
            notifyTradeExecution(
                'Trade Executed',
                `SELL ${qty} ${holding.ticker_symbol} at ${holding.current_price?.toFixed(2) || 'market'}`,
                'LOW'
            );
            if (onAlert) {
                const message = `SELL ${qty} ${holding.ticker_symbol} executed${holding.current_price ? ` at ${formatCurrency(holding.current_price)}` : ''}`;
                const details = [
                    holding.current_price ? `Price: ${formatCurrency(holding.current_price)}` : 'Price: market',
                    `Qty: ${qty}`
                ].join(' • ');
                onAlert({
                    id: `sell-${holding.ticker_symbol}-${Date.now()}`,
                    title: 'Trade Executed',
                    message,
                    details,
                    sourceType: 'trade',
                    sourceId: `sell-${holding.ticker_symbol}-${Date.now()}`,
                    type: 'trade_executed',
                    severity: 'LOW',
                    timestamp: Date.now()
                });
            }
            fetchData();
            setShowSellModal(false);
            setSelectedHolding(null);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSubmittingSell(false);
        }
    };

    const handleResetPortfolio = async () => {
        if (!window.confirm('Are you sure you want to reset your portfolio? This will delete all trades and holdings and reset your balance to $500,000.')) {
            return;
        }

        try {
            const response = await fetch(
                `${API_BASE}/api/paper-trading/profiles/${profileId}/portfolio/reset`,
                {
                    method: 'POST',
                    headers: authHeaders
                }
            );

            if (!response.ok) {
                throw new Error('Failed to reset portfolio');
            }

            toast.success('Portfolio reset successfully');
            fetchData();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleCancelOrder = async (orderId) => {
        try {
            setCancelingOrderId(orderId);
            const response = await fetch(
                `${API_BASE}/api/paper-trading/profiles/${profileId}/trades/${orderId}/cancel`,
                { method: 'POST', headers: authHeaders }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to cancel order');
            }
            toast.success('Limit order canceled');
            fetchData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setCancelingOrderId(null);
        }
    };

    const openEditOrderModal = (order) => {
        setEditingOrder(order);
        setEditQuantity(order.quantity?.toString() || '');
        setEditLimitPrice(order.limit_price?.toString() || '');
        setEditExpiry(order.expires_at ? new Date(order.expires_at).toISOString() : '');
        setEditExpiryPreset('custom');
        setShowEditOrderModal(true);
    };

    const addMonths = (date, months) => {
        const next = new Date(date);
        const day = next.getDate();
        next.setMonth(next.getMonth() + months);
        if (next.getDate() < day) {
            next.setDate(0);
        }
        return next;
    };

    const computeExpiryFromPreset = (preset) => {
        if (preset === 'custom') {
            return editExpiry || '';
        }
        if (preset === 'infinite') {
            return '';
        }
        const now = new Date();
        const target = new Date(now);
        if (preset === 'today') {
            target.setHours(16, 0, 0, 0);
            if (now > target) {
                target.setDate(target.getDate() + 1);
                target.setHours(16, 0, 0, 0);
            }
        } else if (preset === 'tomorrow') {
            target.setDate(target.getDate() + 1);
            target.setHours(16, 0, 0, 0);
        } else if (preset === 'week') {
            target.setDate(target.getDate() + 7);
            target.setHours(16, 0, 0, 0);
        } else if (preset === 'month') {
            const next = addMonths(target, 1);
            next.setHours(16, 0, 0, 0);
            return next.toISOString();
        } else if (preset === 'quarter') {
            const next = addMonths(target, 3);
            next.setHours(16, 0, 0, 0);
            return next.toISOString();
        } else if (preset === 'year_end') {
            target.setMonth(11, 31);
            target.setHours(16, 0, 0, 0);
        }
        return target.toISOString();
    };

    const handleEditExpiryPresetChange = (value) => {
        setEditExpiryPreset(value);
        const computed = computeExpiryFromPreset(value);
        setEditExpiry(computed);
    };

    const handleUpdateOrder = async () => {
        if (!editingOrder) return;
        try {
            setSavingEdit(true);
            const payload = {
                quantity: editQuantity ? parseInt(editQuantity, 10) : null,
                limit_price: editLimitPrice ? parseFloat(editLimitPrice) : null,
                expires_at: editExpiry ? new Date(editExpiry).toISOString() : null
            };
            const response = await fetch(
                `${API_BASE}/api/paper-trading/profiles/${profileId}/trades/${editingOrder.id}`,
                {
                    method: 'PUT',
                    headers: authHeaders,
                    body: JSON.stringify(payload)
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to update order');
            }
            toast.success('Limit order updated');
            setShowEditOrderModal(false);
            setEditingOrder(null);
            fetchData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSavingEdit(false);
        }
    };

    const openEditRiskModal = (order) => {
        setEditingRiskOrder(order);
        setEditStopLoss(order.stop_loss !== null && order.stop_loss !== undefined ? order.stop_loss.toString() : '');
        setEditTakeProfit(order.take_profit !== null && order.take_profit !== undefined ? order.take_profit.toString() : '');
        setShowEditRiskModal(true);
    };

    const handleUpdateRisk = async (clearOnly = false, orderOverride = null) => {
        const targetOrder = orderOverride || editingRiskOrder;
        if (!targetOrder) return;
        try {
            setSavingRisk(true);
            const payload = {
                stop_loss: clearOnly ? null : (editStopLoss ? parseFloat(editStopLoss) : null),
                take_profit: clearOnly ? null : (editTakeProfit ? parseFloat(editTakeProfit) : null)
            };
            const response = await fetch(
                `${API_BASE}/api/paper-trading/profiles/${profileId}/trades/${targetOrder.id}`,
                {
                    method: 'PUT',
                    headers: authHeaders,
                    body: JSON.stringify(payload)
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to update risk settings');
            }
            toast.success(clearOnly ? 'Risk settings removed' : 'Risk settings updated');
            setShowEditRiskModal(false);
            setEditingRiskOrder(null);
            fetchData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSavingRisk(false);
        }
    };

    const openTradeHistory = () => {
        setShowTradeHistory(true);
        fetchTradeHistory();
    };

    const clearTradeHistoryFilters = () => {
        setTradeHistoryDate('');
        setTradeHistoryFromDate('');
        setTradeHistoryToDate('');
        setTradeHistorySymbol('');
        setTradeHistoryType('');
        setTradeHistoryOrder('');
        setTradeHistoryStatus('');
    };

    const openSellModal = (holding) => {
        const initialQty = sellQuantity[holding.ticker_symbol] || holding.quantity;
        setSelectedHolding(holding);
        setModalQuantity(initialQty);
        setShowSellModal(true);
    };

    const getAllocatedRiskQuantity = (tickerSymbol) => {
        if (!tickerSymbol) return 0;
        return openRiskSettings.reduce((total, order) => {
            if (order.ticker_symbol === tickerSymbol) {
                return total + (order.quantity || 0);
            }
            return total;
        }, 0);
    };

    const getAvailableRiskQuantity = (holding) => {
        if (!holding) return 0;
        const allocated = getAllocatedRiskQuantity(holding.ticker_symbol);
        return Math.max(holding.quantity - allocated, 0);
    };

    const closeSellModal = () => {
        if (submittingSell) return;
        setShowSellModal(false);
        setSelectedHolding(null);
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return '-';
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(2)}%`;
    };

    const getProfitLossColor = (value) => {
        if (value === null || value === undefined) return '';
        return value >= 0 ? 'text-success' : 'text-danger';
    };

    const openCreateRiskModal = (holding) => {
        const available = getAvailableRiskQuantity(holding);
        setCreateRiskHolding(holding);
        setCreateRiskQuantity(available > 0 ? available : '');
        setCreateRiskStopLoss('');
        setCreateRiskTakeProfit('');
        setShowCreateRiskModal(true);
    };

    const closeCreateRiskModal = () => {
        if (savingCreateRisk) return;
        setShowCreateRiskModal(false);
        setCreateRiskHolding(null);
    };

    const handleCreateRiskSettings = async () => {
        if (!createRiskHolding) return;
        const quantity = parseInt(createRiskQuantity, 10);
        const available = getAvailableRiskQuantity(createRiskHolding);
        if (!quantity || quantity <= 0 || quantity > available) {
            toast.error(`Quantity must be between 1 and ${available}`);
            return;
        }
        if (!createRiskStopLoss && !createRiskTakeProfit) {
            toast.error('Provide stop loss or take profit');
            return;
        }

        try {
            setSavingCreateRisk(true);
            const payload = {
                ticker_symbol: createRiskHolding.ticker_symbol,
                quantity,
                stop_loss: createRiskStopLoss ? parseFloat(createRiskStopLoss) : null,
                take_profit: createRiskTakeProfit ? parseFloat(createRiskTakeProfit) : null
            };
            const response = await fetch(
                `${API_BASE}/api/paper-trading/profiles/${profileId}/holdings/${createRiskHolding.ticker_symbol}/risk-settings`,
                {
                    method: 'POST',
                    headers: authHeaders,
                    body: JSON.stringify(payload)
                }
            );
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to create risk settings');
            }
            toast.success('Risk settings created');
            setShowCreateRiskModal(false);
            setCreateRiskHolding(null);
            fetchData();
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSavingCreateRisk(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Loading portfolio...</p>
            </div>
        );
    }

    return (
        <div>
            {/* Action Buttons */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Your Holdings</h5>
                <div>
                    <Button variant="outline-secondary" size="sm" onClick={openTradeHistory} className="me-2">
                        Trade History
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={onRefresh} className="me-2">
                        Refresh
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={handleResetPortfolio}>
                        Reset Portfolio
                    </Button>
                </div>
            </div>

            {/* Holdings Table */}
            {openRiskSettings.length > 0 && (
                <Card className="mb-3">
                    <Card.Header className="py-2 bg-white border-bottom">
                        <strong>Open Risk Settings</strong>
                    </Card.Header>
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Symbol</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Order</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end">Stop Loss</th>
                                <th className="text-end">Current</th>
                                <th className="text-end">Take Profit</th>
                                <th>Opened</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {openRiskSettings.map((order) => (
                                <tr key={order.id}>
                                    <td>
                                        <a
                                            href={`/stock/${order.ticker_symbol}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-decoration-none fw-bold"
                                        >
                                            {order.ticker_symbol}
                                        </a>
                                    </td>
                                    <td>
                                        <Badge bg={order.trade_type === 'BUY' ? 'success' : 'danger'}>
                                            {order.trade_type}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={order.status === 'OPEN' ? 'warning' : order.status === 'CANCELED' ? 'danger' : 'secondary'}>
                                            {order.status}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg={order.order_type === 'LIMIT' ? 'warning' : 'secondary'}>
                                            {order.order_type || 'MARKET'}
                                        </Badge>
                                    </td>
                                    <td className="text-end">{order.quantity}</td>
                                    <td className="text-end">
                                        {order.stop_loss !== null && order.stop_loss !== undefined
                                            ? formatCurrency(order.stop_loss)
                                            : '-'}
                                    </td>
                                    <td className="text-end">
                                        <span className={
                                            order.stop_loss !== null && order.stop_loss !== undefined &&
                                            order.take_profit !== null && order.take_profit !== undefined &&
                                            holdings.find(h => h.ticker_symbol === order.ticker_symbol)?.current_price !== undefined
                                                ? (holdings.find(h => h.ticker_symbol === order.ticker_symbol).current_price >= order.take_profit
                                                    ? 'text-success'
                                                    : holdings.find(h => h.ticker_symbol === order.ticker_symbol).current_price <= order.stop_loss
                                                        ? 'text-danger'
                                                        : 'text-warning')
                                                : ''
                                        }>
                                            {(() => {
                                                const currentPrice = holdings.find(h => h.ticker_symbol === order.ticker_symbol)?.current_price;
                                                return currentPrice !== undefined && currentPrice !== null
                                                    ? formatCurrency(currentPrice)
                                                    : '-';
                                            })()}
                                        </span>
                                    </td>
                                    <td className="text-end">
                                        {order.take_profit !== null && order.take_profit !== undefined
                                           ? formatCurrency(order.take_profit)
                                           : '-'}
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            {formatToLocalTime(order.created_at, dateTimeFormatOptions)}
                                        </small>
                                    </td>
                                    <td>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            className="me-2"
                                            onClick={() => openEditRiskModal(order)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => {
                                                if (!window.confirm(`Remove risk settings for ${order.ticker_symbol}?`)) {
                                                    return;
                                                }
                                                handleUpdateRisk(true, order);
                                            }}
                                        >
                                            Remove
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
            {openLimitOrders.length > 0 && (
                <Card className="mb-3">
                    <Card.Header className="py-2 bg-white border-bottom">
                        <strong>Open Limit Orders</strong>
                    </Card.Header>
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Symbol</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end">Limit Price</th>
                                <th>Valid Until</th>
                                <th>Placed</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {openLimitOrders.map((order) => (
                                <tr key={order.id}>
                                    <td>
                                        <a
                                            href={`/stock/${order.ticker_symbol}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-decoration-none fw-bold"
                                        >
                                            {order.ticker_symbol}
                                        </a>
                                    </td>
                                    <td>
                                        <Badge bg={order.trade_type === 'BUY' ? 'success' : 'danger'}>
                                            {order.trade_type}
                                        </Badge>
                                    </td>
                                    <td>
                                        <Badge bg="warning">OPEN</Badge>
                                    </td>
                                    <td className="text-end">{order.quantity}</td>
                                    <td className="text-end">
                                        {order.limit_price !== null && order.limit_price !== undefined
                                            ? formatCurrency(order.limit_price)
                                            : '-'}
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            {formatToLocalTime(order.expires_at, dateTimeFormatOptions)}
                                        </small>
                                    </td>
                                    <td>
                                        <small className="text-muted">
                                            {formatToLocalTime(order.created_at, dateTimeFormatOptions)}
                                        </small>
                                    </td>
                                    <td>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            disabled={cancelingOrderId === order.id}
                                            onClick={() => handleCancelOrder(order.id)}
                                        >
                                            {cancelingOrderId === order.id ? 'Canceling...' : 'Cancel'}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            className="ms-2"
                                            onClick={() => openEditOrderModal(order)}
                                        >
                                            Edit
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
            {holdings.length > 0 ? (
                <Card>
                    <Table responsive hover className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Symbol</th>
                                <th>Order</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end">Avg Cost</th>
                                <th className="text-end">Current Price</th>
                                <th className="text-end">Value</th>
                                <th className="text-end">P&L</th>
                                <th className="text-end">P&L %</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {holdingDisplayRows.map((holding) => (
                                <tr key={holding.display_key}>
                                    <td>
                                        <a
                                            href={`/stock/${holding.ticker_symbol}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-decoration-none fw-bold"
                                        >
                                            {holding.ticker_symbol}
                                        </a>
                                    </td>
                                    <td>
                                        {holding.display_order_type && holding.display_order_type !== '-' ? (
                                            <Badge
                                                bg={holding.display_order_type === 'LIMIT' ? 'warning' : 'secondary'}
                                            >
                                                {holding.display_order_type}
                                            </Badge>
                                        ) : (
                                            <span className="text-muted">—</span>
                                        )}
                                    </td>
                                    <td className="text-end">{holding.quantity}</td>
                                    <td className="text-end">{formatCurrency(holding.average_cost)}</td>
                                    <td className="text-end">{formatCurrency(holding.current_price)}</td>
                                    <td className="text-end">{formatCurrency(holding.current_value)}</td>
                                    <td className={`text-end ${getProfitLossColor(holding.profit_loss)}`}>
                                        {formatCurrency(holding.profit_loss)}
                                    </td>
                                    <td className={`text-end ${getProfitLossColor(holding.profit_loss_percent)}`}>
                                        {formatPercent(holding.profit_loss_percent)}
                                    </td>
                                    <td className="text-center">
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => openSellModal(holding)}
                                            >
                                                Sell
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => openCreateRiskModal(holding)}
                                                disabled={getAvailableRiskQuantity(holding) === 0}
                                            >
                                                Add SL/TP
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            ) : (
                <Alert variant="info">
                    <Alert.Heading>No Holdings Yet</Alert.Heading>
                    <p className="mb-0">
                        Start paper trading by visiting any stock page and clicking the "Buy" button.
                        You start with ${portfolio?.initial_balance?.toLocaleString() || '500,000'} virtual cash.
                    </p>
                </Alert>
            )}

            {/* Trade History Modal */}
            <Modal show={showTradeHistory} onHide={() => setShowTradeHistory(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Trade History</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Row className="g-2 mb-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={tradeHistoryDate}
                                    onChange={(e) => setTradeHistoryDate(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small mb-1">From</Form.Label>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={tradeHistoryFromDate}
                                    onChange={(e) => {
                                        setTradeHistoryDate('');
                                        setTradeHistoryFromDate(e.target.value);
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small mb-1">To</Form.Label>
                                <Form.Control
                                    type="date"
                                    size="sm"
                                    value={tradeHistoryToDate}
                                    onChange={(e) => {
                                        setTradeHistoryDate('');
                                        setTradeHistoryToDate(e.target.value);
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={3} className="d-flex align-items-end justify-content-end gap-2">
                            <Button variant="outline-secondary" size="sm" onClick={clearTradeHistoryFilters}>
                                Clear
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={fetchTradeHistory} disabled={tradeHistoryLoading}>
                                Refresh
                            </Button>
                        </Col>
                    </Row>
                    <Row className="g-2 mb-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Symbol</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={tradeHistorySymbol}
                                    onChange={(e) => setTradeHistorySymbol(e.target.value)}
                                >
                                    <option value="">All symbols</option>
                                    {tradeHistorySymbols.map((symbol) => (
                                        <option key={`th-symbol-${symbol}`} value={symbol}>
                                            {symbol}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Type</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={tradeHistoryType}
                                    onChange={(e) => setTradeHistoryType(e.target.value)}
                                >
                                    <option value="">All types</option>
                                    <option value="BUY">BUY</option>
                                    <option value="SELL">SELL</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Order</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={tradeHistoryOrder}
                                    onChange={(e) => setTradeHistoryOrder(e.target.value)}
                                >
                                    <option value="">All orders</option>
                                    {tradeHistoryOrders.map((orderType) => (
                                        <option key={`th-order-${orderType}`} value={orderType}>
                                            {orderType}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label className="small mb-1">Status</Form.Label>
                                <Form.Select
                                    size="sm"
                                    value={tradeHistoryStatus}
                                    onChange={(e) => setTradeHistoryStatus(e.target.value)}
                                >
                                    <option value="">All statuses</option>
                                    {tradeHistoryStatuses.map((status) => (
                                        <option key={`th-status-${status}`} value={status}>
                                            {status}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {tradeHistoryLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                            <p className="text-muted mt-2 mb-0">Loading trade history...</p>
                        </div>
                    ) : tradeHistoryError ? (
                        <Alert variant="danger" className="mb-0">{tradeHistoryError}</Alert>
                    ) : filteredTradeHistory.length > 0 ? (
                        <>
                        <div className="small text-muted mb-2">
                            Showing {filteredTradeHistory.length} of {tradeHistoryTrades.length} trades
                        </div>
                        <Table responsive hover size="sm">
                            <thead className="table-light">
                                <tr>
                                    <th>Date</th>
                                    <th>Symbol</th>
                                    <th>Type</th>
                                    <th>Order</th>
                                    <th className="text-end">Qty</th>
                                    <th className="text-end">Entry Price</th>
                                    <th className="text-end">Exit Price</th>
                                    <th className="text-end">P&L</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTradeHistory.map((trade) => (
                                    <tr key={trade.id}>
                                        <td>
                                            <small>
                                                {formatToLocalTime(trade.created_at, dateTimeFormatOptions)}
                                            </small>
                                        </td>
                                        <td className="fw-bold">{trade.ticker_symbol}</td>
                                        <td>
                                            <Badge bg={trade.trade_type === 'BUY' ? 'success' : 'danger'}>
                                                {trade.trade_type}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={trade.order_type === 'LIMIT' ? 'warning' : 'secondary'}>
                                                {trade.order_type || 'MARKET'}
                                            </Badge>
                                        </td>
                                        <td className="text-end">{trade.quantity}</td>
                                        <td className="text-end">{formatCurrency(trade.entry_price)}</td>
                                        <td className="text-end">{trade.exit_price ? formatCurrency(trade.exit_price) : '-'}</td>
                                        <td className={`text-end ${getProfitLossColor(trade.profit_loss)}`}>
                                            {trade.profit_loss !== null ? formatCurrency(trade.profit_loss) : '-'}
                                        </td>
                                        <td>
                                            <Badge bg={trade.status === 'OPEN' ? 'warning' : trade.status === 'CANCELED' ? 'danger' : 'secondary'}>
                                                {trade.status}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        </>
                    ) : (
                        <p className="text-center text-muted">No trades yet</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTradeHistory(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Sell Confirmation Modal */}
            <Modal show={showSellModal} onHide={closeSellModal} centered>
                <Modal.Header closeButton={!submittingSell}>
                    <Modal.Title>Confirm Sell</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedHolding ? (
                        <>
                            <p className="mb-2">
                                Sell <strong>{selectedHolding.ticker_symbol}</strong> shares?
                            </p>
                            <div className="d-flex align-items-center gap-2">
                                <label className="form-label mb-0" htmlFor="sell-qty-input">
                                    Quantity
                                </label>
                                <input
                                    id="sell-qty-input"
                                    type="number"
                                    min="1"
                                    max={selectedHolding.quantity}
                                    value={modalQuantity}
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value, 10);
                                        setModalQuantity(Number.isNaN(value) ? 0 : value);
                                        setSellQuantity({
                                            ...sellQuantity,
                                            [selectedHolding.ticker_symbol]: Number.isNaN(value) ? 0 : value
                                        });
                                    }}
                                    className="form-control form-control-sm"
                                    style={{ width: '120px' }}
                                    disabled={submittingSell}
                                />
                                <small className="text-muted">
                                    Max {selectedHolding.quantity}
                                </small>
                            </div>
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeSellModal} disabled={submittingSell}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => handleSell(selectedHolding, modalQuantity)}
                        disabled={!selectedHolding || submittingSell}
                    >
                        {submittingSell ? 'Selling...' : 'Confirm Sell'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create Risk Settings Modal */}
            <Modal show={showCreateRiskModal} onHide={closeCreateRiskModal} centered>
                <Modal.Header closeButton={!savingCreateRisk}>
                    <Modal.Title>Create SL/TP</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {createRiskHolding ? (
                        <>
                            <p className="mb-2">
                                Set SL/TP for <strong>{createRiskHolding.ticker_symbol}</strong>
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Quantity</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="1"
                                    max={getAvailableRiskQuantity(createRiskHolding)}
                                    value={createRiskQuantity}
                                    onChange={(e) => setCreateRiskQuantity(e.target.value)}
                                    disabled={savingCreateRisk}
                                />
                                <Form.Text className="text-muted">
                                    Available for SL/TP: {getAvailableRiskQuantity(createRiskHolding)}
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Stop Loss</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={createRiskStopLoss}
                                    onChange={(e) => setCreateRiskStopLoss(e.target.value)}
                                    disabled={savingCreateRisk}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Take Profit</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={createRiskTakeProfit}
                                    onChange={(e) => setCreateRiskTakeProfit(e.target.value)}
                                    disabled={savingCreateRisk}
                                />
                            </Form.Group>
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeCreateRiskModal} disabled={savingCreateRisk}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateRiskSettings} disabled={savingCreateRisk}>
                        {savingCreateRisk ? 'Saving...' : 'Create'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Limit Order Modal */}
            <Modal show={showEditOrderModal} onHide={() => setShowEditOrderModal(false)} centered>
                <Modal.Header closeButton={!savingEdit}>
                    <Modal.Title>Edit Limit Order</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Quantity</Form.Label>
                        <Form.Control
                            type="number"
                            min="1"
                            value={editQuantity}
                            onChange={(e) => setEditQuantity(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Limit Price</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            value={editLimitPrice}
                            onChange={(e) => setEditLimitPrice(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Valid Until</Form.Label>
                        <Form.Select
                            value={editExpiryPreset}
                            onChange={(e) => handleEditExpiryPresetChange(e.target.value)}
                        >
                            <option value="custom">Custom (Current)</option>
                            <option value="today">Today (End of Trading Day)</option>
                            <option value="tomorrow">Tomorrow (End of Trading Day)</option>
                            <option value="week">1 Week</option>
                            <option value="month">1 Month</option>
                            <option value="quarter">3 Months</option>
                            <option value="year_end">End of This Year</option>
                            <option value="infinite">Infinite</option>
                        </Form.Select>
                        <Form.Text className="text-muted d-block mt-1">
                            {editExpiryPreset === 'infinite'
                                ? 'Order will remain active until manually canceled.'
                                : `Expires at: ${editExpiry ? formatToLocalTime(new Date(editExpiry).toISOString()) : '-'}`}
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditOrderModal(false)} disabled={savingEdit}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateOrder} disabled={savingEdit}>
                        {savingEdit ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Risk Settings Modal */}
            <Modal show={showEditRiskModal} onHide={() => setShowEditRiskModal(false)} centered>
                <Modal.Header closeButton={!savingRisk}>
                    <Modal.Title>Edit Risk Settings</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Stop Loss</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            value={editStopLoss}
                            onChange={(e) => setEditStopLoss(e.target.value)}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Take Profit</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            value={editTakeProfit}
                            onChange={(e) => setEditTakeProfit(e.target.value)}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditRiskModal(false)} disabled={savingRisk}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={() => handleUpdateRisk(false)} disabled={savingRisk}>
                        {savingRisk ? 'Saving...' : 'Save'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default HoldingsManager;
